import { AlertTriangleIcon, FileSlidersIcon, InfoIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { Focusable } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";

import { extendResult } from "@blis/shared/models/result";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label, TextArea, TextField } from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { ItemDetail } from "@/components/ui/list-box";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { Sheet } from "@/components/ui/sheet";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import type { TTestOrder } from "@/lib/types";
import { useAccessionReport } from "./ReportContext";

type FormValues = {
  selectedTests: number[];
  status: "preliminary" | "final" | "corrected";
  comment: string;
};

export function ReportSettingsFlyout() {
  const {
    testOrders,
    displayedTests,
    setDisplayedTests,
    status,
    setStatus,
    comment,
    setComment,
  } = useAccessionReport();
  const [open, setOpen] = useState(false);

  const filteredOrders = testOrders?.filter(
    (order) =>
      !order.isReflex || (order.isReflex && order.testSpec.shouldReportReflex),
  );

  const onSave = useCallback(
    ({ selectedTests, status, comment }: FormValues) => {
      if (selectedTests.length === 0) {
        toast.error("Please select at least one test");
        return;
      }
      setDisplayedTests(selectedTests);
      setStatus(status);
      setComment(comment);
      setOpen(false);
    },
    [setDisplayedTests, setStatus, setComment],
  );

  return (
    <DialogTrigger>
      <TooltipTrigger>
        <Button
          variant="outline"
          size="icon"
          onPress={() => setOpen(true)}
          className="size-6 inline-flex lg:hidden"
        >
          <FileSlidersIcon className="size-3" />
        </Button>
        <Tooltip>Report settings</Tooltip>
      </TooltipTrigger>
      <Sheet isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Report Settings</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ReportSettingsForm
              testOrders={filteredOrders}
              onSave={onSave}
              defaultValues={{
                selectedTests: displayedTests,
                status,
                comment,
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="submit" form="report-settings-form">
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Sheet>
    </DialogTrigger>
  );
}

export function ReportSettingsView() {
  const {
    testOrders,
    displayedTests,
    setDisplayedTests,
    status,
    setStatus,
    comment,
    setComment,
  } = useAccessionReport();

  const filteredOrders = testOrders?.filter(
    (order) =>
      !order.isReflex || (order.isReflex && order.testSpec.shouldReportReflex),
  );

  const onSave = useCallback(
    ({ selectedTests, status, comment }: FormValues) => {
      if (selectedTests.length === 0) {
        toast.error("Please select at least one test");
        return;
      }
      setDisplayedTests(selectedTests);
      setStatus(status);
      setComment(comment);
    },
    [setDisplayedTests, setStatus, setComment],
  );

  return (
    <>
      <ReportSettingsForm
        testOrders={filteredOrders}
        onSave={onSave}
        defaultValues={{
          selectedTests: displayedTests,
          status,
          comment,
        }}
      />
      <div className="flex justify-end">
        <Button
          type="submit"
          form="report-settings-form"
          className="mt-4"
          variant="outline"
        >
          Update
        </Button>
      </div>
    </>
  );
}

function ReportSettingsForm({
  testOrders,
  defaultValues,
  onSave,
}: {
  testOrders: TTestOrder[];
  onSave: (values: FormValues) => void;
  defaultValues?: FormValues;
}) {
  const report = useAccessionReport();
  const isCorrected = report.status === "corrected";
  const extendedOrders = testOrders.map((order) => ({
    ...order,
    results: order.results.map(extendResult),
  }));

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: defaultValues ?? {
      selectedTests: extendedOrders.map((t) => t.id),
      status: "preliminary",
      comment: "",
    },
  });

  const [selectedTests, status] = watch(["selectedTests", "status"]);

  const areSelectedResultsFinal = useCallback(
    (tests: number[]) => {
      const selectedResults = extendedOrders
        .filter((order) => tests.includes(order.id))
        .flatMap((order) => order.results);

      return selectedResults.every((result) => !!result.$final);
    },
    [extendedOrders],
  );

  const syncFinalOverride = useCallback(
    (tests: number[]) => {
      if (isCorrected) {
        return;
      }
      setValue(
        "status",
        areSelectedResultsFinal(tests) ? "final" : "preliminary",
      );
    },
    [setValue, areSelectedResultsFinal, isCorrected],
  );

  const getDisabledKeys = useCallback(() => {
    if (selectedTests.length > 1) {
      return [];
    }
    return extendedOrders
      .filter((order) => order.id === selectedTests[0])
      .map((order) => order.id.toString());
  }, [selectedTests, extendedOrders]);

  const shouldDisplayWarning =
    !areSelectedResultsFinal(selectedTests) && status === "final";

  return (
    <form
      className="flex flex-col gap-6"
      id="report-settings-form"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label id="selected-tests-label">Selected tests</Label>
            <TooltipTrigger delay={100}>
              <Focusable>
                <InfoIcon className="text-muted-foreground size-4 cursor-pointer" />
              </Focusable>
              <Tooltip>Select specific tests to include in the report.</Tooltip>
            </TooltipTrigger>
          </div>
          <span
            className={
              selectedTests.length < extendedOrders.length
                ? "text-xs font-medium text-amber-600"
                : "text-xs text-muted-foreground"
            }
          >
            {selectedTests.length} / {extendedOrders.length}
          </span>
        </div>
        <Controller
          control={control}
          name="selectedTests"
          render={({ field }) => (
            <GridList
              items={extendedOrders || []}
              selectionMode="multiple"
              selectedKeys={field.value.map((id) => id.toString())}
              aria-labelledby="selected-tests-label"
              disabledKeys={getDisabledKeys()}
              onSelectionChange={(keys) => {
                if (typeof keys !== "string" && keys.size === 0) {
                  // disallow deselecting all tests
                  return;
                }
                const keyNums =
                  keys === "all"
                    ? extendedOrders.map((t) => t.id)
                    : Array.from(keys).map(Number);
                field.onChange(keyNums);
                syncFinalOverride(keyNums);
              }}
              className="max-h-75 overflow-y-auto"
            >
              {(test) => (
                <GridListItem
                  key={test.id}
                  id={test.id.toString()}
                  textValue={`${test.testSpec.name} (${test.testSpec.code})`}
                >
                  <Text className="min-w-0 truncate" size="1">
                    {test.testSpec.name}
                  </Text>
                  <ItemDetail className="text-2xs pl-3">
                    {test.testSpec.code}
                  </ItemDetail>
                </GridListItem>
              )}
            </GridList>
          )}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <RadioGroup
              id="status-radio"
              value={field.value}
              onChange={field.onChange}
              orientation="vertical"
            >
              <Label>Status</Label>
              <Radio value="preliminary" id="status-preliminary">
                <Badge size="sm" color="yellow">
                  Preliminary
                </Badge>
              </Radio>
              <Radio value="final" id="status-final">
                <Badge size="sm" color="gray">
                  Final
                </Badge>
              </Radio>
              <Radio value="corrected" id="status-corrected">
                <Badge size="sm" color="red">
                  Corrected
                </Badge>
              </Radio>
            </RadioGroup>
          )}
        />
      </div>
      {shouldDisplayWarning && (
        <div className="flex gap-2 bg-yellow-50/50 rounded border border-amber-500/20 text-amber-800 bg-ambger-50/50 p-2">
          <AlertTriangleIcon className="size-6" />
          <Text size="sm">
            The selected tests include results that are still pending or not
            final.
          </Text>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <TextField
              name="comment"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              validationBehavior="aria"
              className="w-full"
            >
              <Label>Comment</Label>
              <TextArea placeholder="Enter a comment..." />
            </TextField>
          )}
        />
      </div>
    </form>
  );
}
